import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { auth } from '../firebaseConfig';
import { useAuth, signIn, sendVerification } from '../AuthContext';
import AuthShell from '../components/AuthShell';

// Where the links in our own verification / password-reset emails land
// (api/authMailApi.js rewrites Firebase's action link onto this page):
//   /auth/action?mode=verifyEmail&oobCode=…&continueUrl=/path
//   /auth/action?mode=resetPassword&oobCode=…&continueUrl=/login
// Firebase still owns the code; we apply it here so the person stays on
// nventr.io and the token refreshes before the console renders.

// Only a same-origin path may be the continue target.
const safeNext = (s, fallback) => (/^\/(?!\/)[^\s]*$/.test(String(s || '')) ? s : fallback);

export default function AuthAction() {
  const { search } = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const q = new URLSearchParams(search);
  const mode = q.get('mode') || '';
  const code = q.get('oobCode') || '';
  const next = safeNext(q.get('continueUrl'), mode === 'resetPassword' ? '/login' : '/console');

  const [state, setState] = useState('working'); // working | verified | reset-form | done | error
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!code || !mode) { setState('error'); setError('This link is incomplete. Open it again from the email, or request a new one.'); return; }
      try {
        if (mode === 'verifyEmail') {
          await auth.applyActionCode(code);
          const u = auth.currentUser;
          if (u) {
            await u.reload().catch(() => {});
            await u.getIdToken(true).catch(() => {});
            if (alive) navigate(next, { replace: true });
            return;
          }
          if (alive) setState('verified');
        } else if (mode === 'resetPassword') {
          const addr = await auth.verifyPasswordResetCode(code);
          if (alive) { setEmail(addr); setState('reset-form'); }
        } else {
          if (alive) { setState('error'); setError(`Unknown action "${mode}".`); }
        }
      } catch (e) {
        if (!alive) return;
        setState('error');
        setError(e?.code === 'auth/expired-action-code' || e?.code === 'auth/invalid-action-code'
          ? 'This link has expired or was already used. Request a new one below.'
          : (e?.message || 'Something went wrong.'));
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code, mode]);

  const submitReset = async (e) => {
    e.preventDefault();
    if (password.length < 8) { setError('Choose at least 8 characters.'); return; }
    setBusy(true); setError('');
    try {
      await auth.confirmPasswordReset(code, password);
      await signIn(email, password).catch(() => {});
      setState('done');
      navigate(auth.currentUser ? '/console' : `/login?next=${encodeURIComponent(next)}`, { replace: true });
    } catch (err) {
      setError(err?.message || 'Could not set the password.');
    } finally { setBusy(false); }
  };

  const resend = async () => {
    setBusy(true); setError('');
    try { await sendVerification(auth.currentUser, next); setError('A new link is on its way.'); } catch (err) { setError(err.message); } finally { setBusy(false); }
  };

  const title = mode === 'resetPassword' ? 'Choose a new password' : 'Verifying your email';

  return (
    <AuthShell>
      <div className="cs-modal" style={{ maxWidth: 420 }}>
        <h2 style={{ fontSize: 22, marginTop: 0 }}>{title}</h2>
        {state === 'working' && <p style={{ color: 'var(--muted)', fontSize: 14 }}>One moment…</p>}

        {state === 'verified' && (
          <>
            <p style={{ color: 'var(--muted)', fontSize: 14, marginTop: 0 }}>Your email is verified. Sign in to continue.</p>
            <Link className="cs-btn cs-btn-primary" to={`/login?next=${encodeURIComponent(next)}`}>Sign in</Link>
          </>
        )}

        {state === 'reset-form' && (
          <form onSubmit={submitReset}>
            <p style={{ color: 'var(--muted)', fontSize: 14, marginTop: 0 }}>For <strong style={{ color: 'var(--text)' }}>{email}</strong>.</p>
            <div className="cs-field">
              <label htmlFor="newpw">New password</label>
              <input id="newpw" type="password" className="cs-input" value={password} autoComplete="new-password" autoFocus onChange={(e) => setPassword(e.target.value)} minLength={8} required />
            </div>
            {error && <div className="cs-empty" style={{ padding: 12, marginBottom: 14, color: 'var(--accent)' }}>{error}</div>}
            <button type="submit" className="cs-btn cs-btn-primary" disabled={busy}>{busy ? 'Saving…' : 'Set password and sign in'}</button>
          </form>
        )}

        {state === 'error' && (
          <>
            <div className="cs-empty" style={{ padding: 12, marginBottom: 14, color: 'var(--accent)' }}>{error}</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {mode === 'verifyEmail' && user && !user.isAnonymous && (
                <button type="button" className="cs-btn cs-btn-primary" disabled={busy} onClick={resend}>{busy ? 'Sending…' : 'Send a new link'}</button>
              )}
              {mode === 'resetPassword' && <Link className="cs-btn cs-btn-primary" to="/login">Request a new reset link</Link>}
              <Link className="cs-btn cs-btn-ghost" to="/login">Back to sign in</Link>
            </div>
          </>
        )}
      </div>
    </AuthShell>
  );
}
